import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Heading from "../../../shared_components/Heading/Heading";
import UserData from "../../../services/UserData";
import AIPreEnrichmentService from "../../../services/AIPreEnrichmentService";
import { CollapsibleLists, DetailFields, DetailValue, label } from "./DetailFields";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import style from "./PreEnrichmentRuns.module.css";

const sections = ["winning_rule", "matched_rules", "resolution", "groups", "rules"];

const RunDocumentDetails = () => {
  const { runId, documentId } = useParams();
  const location = useLocation();
  const userData = useContext(UserData);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setData(null);
    setLoading(true);
    setError(false);
    if (!userData) return () => { active = false; };
    AIPreEnrichmentService.getRunDocument(userData, runId, documentId)
      .then(result => { if (active) setData(result); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, runId, documentId, retry]);

  return (
    <section className={layout.container} aria-label="Document Evaluation Details" aria-busy={loading}>
      <div>
        <Link className="btn btn-outline-secondary btn-sm mb-3" to={`/intelligence/pre-enrichment-runs/${encodeURIComponent(runId)}/documents`} state={location.state}>
          <ArrowLeft size={18} aria-hidden="true" /> Back to Run Documents
        </Link>
        <div className={style.documentHeading}>
          <Heading>Document Evaluation Details</Heading>
          {data && <div className={style.proposedStatus} role="status" aria-label="Proposed decision">
            <span className={style.proposedLabel}>Proposed decision</span>
            <span className={style.decisionPill} data-decision={data.document.proposed_decision}>
              {data.document.proposed_decision ? label(data.document.proposed_decision.toLowerCase()) : "Not specified"}
            </span>
          </div>}
        </div>
        <div className={style.runId}>Run: {runId}</div>
      </div>
      <div className={layout.loadingSlot}>
        {loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading document details" />}
      </div>
      <div className={`${layout.content} ${style.documentDetails}`}>
        {error && <div role="alert">Unable to load document details. <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRetry(value => value + 1)}>Retry</button></div>}
        {data && <CollapsibleLists.Provider value={true}>
          <DetailFields data={Object.fromEntries(Object.entries(data.document).filter(([key]) => !sections.includes(key)))} />
          {sections.map(key => <section key={key} className={style.detailSection} aria-label={label(key)}>
            {!Array.isArray(data.document[key]) && <h3>{label(key)}</h3>}
            <DetailValue value={data.document[key]} title={label(key)} />
          </section>)}
        </CollapsibleLists.Provider>}
      </div>
    </section>
  );
};

export default RunDocumentDetails;
